import Dexie, { type EntityTable } from 'dexie';
import type { CompleteMeasurementDto } from '@blagodar/shared';
import { isOwnOperation, supersededBy, toHandOver } from './outbox-rules';

/**
 * Локальная база планшета (IndexedDB).
 *
 * Замерщик приезжает в новостройку, где сети нет ни у него, ни у клиента.
 * Всё, что нужно для составления сметы, лежит здесь: наряды на ближайшие
 * две недели, прайс-лист, фактуры, бригады. Результат замера копится в
 * outbox и уходит на сервер, когда связь появится.
 */

export interface CachedMeasurement {
  id: string;
  dealId: string;
  status: string;
  scheduledAt: string;
  durationMin: number;
  address: string;
  contactPhone: string | null;
  objectType: string | null;
  floor: string | null;
  hasElevator: boolean | null;
  callCenterNotes: string | null;
  surveyorNotes: string | null;
  /** Полный объект с сервера — рисуем карточку без потери полей */
  raw: unknown;
  /** Когда запись положена в кеш */
  cachedAt: number;
}

/** Незавершённый замер: сохраняется при каждом изменении формы. */
export interface MeasurementDraft {
  measurementId: string;
  data: CompleteMeasurementDto;
  updatedAt: number;
  /** Черновик уже отправлен в outbox и ждёт синхронизации */
  submitted: boolean;
}

export interface OutboxOperation {
  /** Идентификатор операции — по нему сервер отсекает повторы */
  clientMutationId: string;
  entityType: 'measurement' | 'client' | 'deal';
  entityId: string;
  operation: 'create' | 'update' | 'complete';
  payload: unknown;
  clientTimestamp: string;
  attempts: number;
  lastError: string | null;
  createdAt: number;
  /**
   * ЧЬЯ ОПЕРАЦИЯ. Пусто — того, кто сейчас вошёл: так заводятся все. Метка
   * ставится при смене человека на планшете (`handOverDevice`): несданное
   * прошлым остаётся его и уходит, когда он войдёт снова, — отправить его
   * под чужим входом сервер не даст («это не ваш наряд»)
   */
  userId?: string | null;
}

export interface CacheEntry {
  key: string;
  value: unknown;
  updatedAt: number;
}

/**
 * Лист, снятый на холсте без сети (правила 171, 183). Глазу замерщика
 * сервер не нужен, а конструктору и чтению цифр — нужен: раньше такой
 * снимок не доезжал никогда, отправка была разовой. Теперь он ждёт здесь
 * и уходит, когда связь появится, вместе с очередью замеров.
 */
export interface PendingPhoto {
  id: string;
  measurementId: string;
  roomId: string | null;
  name: string;
  type: string;
  blob: Blob;
  /** Чей снимок — уходит только под его входом (правило 37) */
  userId: string | null;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

/**
 * РАСХОДКА, СОХРАНЁННАЯ БЕЗ СВЯЗИ (07.10.2026). Монтажник заполняет её в
 * подвале, нажимает «Отправить» — и она ждёт здесь, а уходит сама, когда
 * связь появится, вместе с очередью замеров (`flushCardOps`).
 *
 * НОВАЯ РАСХОДКА — С НОМЕРОМ, ПРИДУМАННЫМ ТЕЛЕФОНОМ (правило 6): отправка,
 * чей ответ потерялся, повторяется, и сервер отдаёт ту же карточку, а не
 * вторую. ПРАВКА ЗАВЕДЁННОЙ — без тела: что набрано, лежит черновиком на
 * устройстве (правило 219), и уходит тот, что там к минуте отправки, —
 * поправленное после «Сохранить» не теряется.
 */
export interface PendingCardOp {
  id: string;
  kind: 'create' | 'update';
  /** Номер расходки: у новой — придуман телефоном */
  cardId: string;
  /** Новая: тело запроса — как ушло бы с экрана */
  body?: Record<string, unknown>;
  /** Новая: что было в форме — вернуть на экран, если сервер откажет */
  form?: { picked: string[]; rows: unknown[]; texts: unknown };
  /** Сразу отправить на склад */
  submit: boolean;
  /** Чья: уходит только под его входом (правило 37) */
  userId: string | null;
  /** По чему человек узнаёт свою расходку: смена и адреса */
  label: string;
  createdAt: number;
  /** Сервер отказал по делу — сама больше не шлётся, ждёт человека */
  refusedAt: number | null;
  lastError: string | null;
  /** Повтор не поможет никогда: расходку выдали или отменили — только убрать */
  final?: boolean;
}

const db = new Dexie('blagodar-crm') as Dexie & {
  measurements: EntityTable<CachedMeasurement, 'id'>;
  drafts: EntityTable<MeasurementDraft, 'measurementId'>;
  outbox: EntityTable<OutboxOperation, 'clientMutationId'>;
  cache: EntityTable<CacheEntry, 'key'>;
  photos: EntityTable<PendingPhoto, 'id'>;
  cardOps: EntityTable<PendingCardOp, 'id'>;
};

db.version(1).stores({
  measurements: 'id, dealId, status, scheduledAt',
  drafts: 'measurementId, updatedAt, submitted',
  outbox: 'clientMutationId, entityId, createdAt',
  cache: 'key',
});

// Вторая версия: очередь листов, снятых без сети. Прежние таблицы не меняются
db.version(2).stores({
  photos: 'id, measurementId, createdAt',
});

// Третья: расходки, сохранённые без связи (07.10.2026)
db.version(3).stores({
  cardOps: 'id, cardId, createdAt',
});

export { db };

// ---------- Справочники ----------

export async function putCache(key: string, value: unknown): Promise<void> {
  await db.cache.put({ key, value, updatedAt: Date.now() });
}

export async function getCache<T>(key: string): Promise<T | null> {
  const entry = await db.cache.get(key);
  return entry ? (entry.value as T) : null;
}

// ---------- Наряды ----------

export async function putMeasurements(items: CachedMeasurement[]): Promise<void> {
  await db.measurements.bulkPut(items);
}

/**
 * Кладёт наряды с сервера И УБИРАЕТ ТЕ, КОТОРЫХ ТАМ БОЛЬШЕ НЕТ.
 *
 * Раньше кэш только пополнялся. Сервер отдаёт живые наряды (запланирован
 * и в работе, ближайшие две недели), а на планшете оставались все, кого
 * он когда-либо видел: отменённый оператором наряд висел «Запланирован»,
 * и замерщик ехал на адрес, куда ехать не надо. Завершённые копились
 * неделями и прятали сегодняшние.
 *
 * НО НЕ ТЕ, ЧТО ЖДУТ ОТПРАВКИ. Замер, завершённый в подвале без связи,
 * лежит в очереди (правило 37) и на сервере ещё не значится живым — стереть
 * его значит потерять работу за день. Такой наряд остаётся, пока очередь
 * не разойдётся.
 */
export async function syncMeasurements(items: CachedMeasurement[]): Promise<void> {
  const fresh = new Set(items.map((m) => m.id));
  const pending = new Set((await db.outbox.toArray()).map((op) => op.entityId));

  const stale = (await db.measurements.toArray())
    .filter((m) => !fresh.has(m.id) && !pending.has(m.id))
    .map((m) => m.id);

  await db.transaction('rw', db.measurements, async () => {
    if (stale.length > 0) await db.measurements.bulkDelete(stale);
    await db.measurements.bulkPut(items);
  });
}

export async function getMeasurement(id: string): Promise<CachedMeasurement | undefined> {
  return db.measurements.get(id);
}

export async function listMeasurements(): Promise<CachedMeasurement[]> {
  const items = await db.measurements.toArray();
  return items.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
}

// ---------- Черновики ----------

export async function saveDraft(
  measurementId: string,
  data: CompleteMeasurementDto,
  /**
   * true — содержимое уже НА СЕРВЕРЕ (форма собрала его из ответа
   * сервера, а не из правок человека). Такой черновик — верная локальная
   * копия отправленного: листы печатают его без плашки «не отправлен».
   * Первая же правка человека перепишет флаг в false — и плашка честно
   * вернётся.
   */
  submitted = false,
): Promise<void> {
  await db.drafts.put({ measurementId, data, updatedAt: Date.now(), submitted });
}

export async function getDraft(measurementId: string): Promise<MeasurementDraft | undefined> {
  return db.drafts.get(measurementId);
}

export async function markDraftSubmitted(measurementId: string): Promise<void> {
  const draft = await db.drafts.get(measurementId);
  if (draft) await db.drafts.put({ ...draft, submitted: true });
}

export async function deleteDraft(measurementId: string): Promise<void> {
  await db.drafts.delete(measurementId);
}

// ---------- Очередь отправки ----------

export async function enqueue(
  op: Omit<OutboxOperation, 'attempts' | 'lastError' | 'createdAt'>,
  /** Кто вошёл — его прежние «Завершить» того же наряда вытесняются */
  currentUserId?: string | null,
): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    /*
     * НОВОЕ «ЗАВЕРШИТЬ» ВЫТЕСНЯЕТ ПРЕЖНЕЕ ТОГО ЖЕ НАРЯДА. Замерщик поправил
     * смету и завершил заново, а прежняя операция — отклонённая или ещё не
     * ушедшая — оставалась в очереди: «ждёт отправки» не гасло (правило
     * 61), а «Повторить» отправило бы устаревшую смету поверх новой.
     * Последнее завершение несёт замер целиком (правило 25), прежнее ему
     * не нужно. Чужие (другого человека на этом планшете) не трогаются
     */
    const older = supersededBy(await db.outbox.where('entityId').equals(op.entityId).toArray(), op, currentUserId);
    if (older.length > 0) await db.outbox.bulkDelete(older);
    await db.outbox.put({ ...op, attempts: 0, lastError: null, createdAt: Date.now() });
  });
}

export async function listOutbox(): Promise<OutboxOperation[]> {
  const items = await db.outbox.toArray();
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

const isOwn = isOwnOperation;

export async function dequeue(clientMutationId: string): Promise<void> {
  await db.outbox.delete(clientMutationId);
}

export async function markOutboxError(clientMutationId: string, error: string): Promise<void> {
  const op = await db.outbox.get(clientMutationId);
  if (!op) return;
  await db.outbox.put({ ...op, attempts: op.attempts + 1, lastError: error });
}

export async function countOutbox(): Promise<number> {
  return db.outbox.count();
}

/** Несданное ДРУГИХ людей на этом планшете — ждёт их входа */
export async function countForeignOutbox(userId: string | null | undefined): Promise<number> {
  return (await db.outbox.toArray()).filter((o) => !isOwn(o, userId)).length;
}

/** Несданное ТОГО, кто вошёл: о чужом ему при выходе не говорят */
export async function countOwnOutbox(userId: string | null | undefined): Promise<number> {
  return (await db.outbox.toArray()).filter((o) => isOwn(o, userId)).length;
}

/**
 * После стольких отклонений сервером операция перестаёт отправляться сама:
 * повтор с тем же payload даёт тот же результат, а вечный ретрай прячет
 * реальную проблему за спамом «Ждут отправки». Дальше — либо человек
 * разбирается сам (кнопка «Повторить»), либо чинят на сервере.
 */
export const MAX_OUTBOX_ATTEMPTS = 5;

/** `userId` — кто вошёл: отправляется только своё (без аргумента — всё, как раньше) */
export async function listPendingOutbox(userId?: string | null): Promise<OutboxOperation[]> {
  const items = await listOutbox();
  return items.filter((o) => o.attempts < MAX_OUTBOX_ATTEMPTS && (userId === undefined || isOwn(o, userId)));
}

export async function listStuckOutbox(userId?: string | null): Promise<OutboxOperation[]> {
  const items = await listOutbox();
  return items.filter((o) => o.attempts >= MAX_OUTBOX_ATTEMPTS && (userId === undefined || isOwn(o, userId)));
}

/** Сбросить счётчик попыток — человек нажал «Повторить» после починки данных. */
export async function resetOutboxAttempts(clientMutationIds: string[]): Promise<void> {
  for (const id of clientMutationIds) {
    const op = await db.outbox.get(id);
    if (op) await db.outbox.put({ ...op, attempts: 0, lastError: null });
  }
}

/**
 * ПЛАНШЕТ ПЕРЕХОДИТ К ДРУГОМУ ЧЕЛОВЕКУ (правило 37).
 *
 * Наряды, черновики и справочники прошлого стираются: там чужие телефоны и
 * адреса. А ОЧЕРЕДЬ ОТПРАВКИ — НЕТ. Раньше стиралась и она, и завершённый
 * в подвале замер прошлого замерщика пропадал целиком — работа за день,
 * молча: предупреждение при выходе висело восемь секунд. Отправить его под
 * входом нового нельзя — сервер ответит «это не ваш наряд», — поэтому
 * несданное помечается прошлым и ждёт его входа; новый видит только число.
 */
export async function handOverDevice(previousUserId: string): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    const stamped = toHandOver(await db.outbox.toArray(), previousUserId);
    if (stamped.length > 0) await db.outbox.bulkPut(stamped);
  });
  await Promise.all([db.measurements.clear(), db.drafts.clear(), db.cache.clear()]);
}

// ---------- Листы, снятые без сети ----------

/** Снимков, отклонённых сервером столько раз, дальше не шлём */
export const MAX_PHOTO_ATTEMPTS = 5;

export async function queuePhoto(p: Omit<PendingPhoto, 'attempts' | 'lastError' | 'createdAt'>): Promise<void> {
  await db.photos.put({ ...p, attempts: 0, lastError: null, createdAt: Date.now() });
}

export async function listPhotos(): Promise<PendingPhoto[]> {
  return (await db.photos.toArray()).sort((a, b) => a.createdAt - b.createdAt);
}

export async function dropPhoto(id: string): Promise<void> {
  await db.photos.delete(id);
}

export async function markPhotoError(id: string, error: string): Promise<void> {
  const p = await db.photos.get(id);
  if (p) await db.photos.put({ ...p, attempts: p.attempts + 1, lastError: error });
}

/** Полная очистка вместе с очередью — для стенда и ручного сброса. */
export async function clearLocalData(): Promise<void> {
  await Promise.all([
    db.measurements.clear(),
    db.drafts.clear(),
    db.outbox.clear(),
    db.cache.clear(),
    db.cardOps.clear(),
  ]);
}
